/**
 * Smile points x Kaching Bundles bridge.
 *
 * Smile's points-on-product-page block only watches for variant/quantity
 * changes when a product has multiple variants or selling plans. TVF's
 * wholesale products are single-variant, so the block never updates when
 * a Kaching deal bar changes the quantity — and Smile can't see Kaching's
 * percentage discounts at all (they apply at checkout).
 *
 * This bridge recomputes the points as:
 *   basePoints x dealQuantity x (1 - discount%)
 * where basePoints is the number Smile itself rendered for 1x undiscounted
 * — so price, currency conversion and earn-rate all stay Smile's problem.
 *
 * The points number is located via Smile's own text template (extracted
 * from its init script, where [points_amount] marks the substitution spot)
 * — never "first number in the text", which would collide with copy like
 * "20 points = $1".
 */
(function () {
  'use strict';

  var SMILE_P_SELECTOR = '[id*="smile_io"] section p';
  var KACHING_SETTINGS_SELECTOR = '.kaching-bundles-deal-block-settings';

  function getDealBars() {
    var el = document.querySelector(KACHING_SETTINGS_SELECTOR);
    if (!el) return null;
    try {
      var cfg = JSON.parse(el.textContent);
      return Array.isArray(cfg.dealBars) && cfg.dealBars.length ? cfg.dealBars : null;
    } catch (e) {
      return null;
    }
  }

  function isMultiVariant() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    return !!(meta && meta.product && meta.product.variants && meta.product.variants.length > 1);
  }

  /* Pull the logged-in/logged-out text templates out of Smile's init script. */
  function getTextTemplates() {
    var scripts = document.querySelectorAll('[id*="smile_io"] script[data-smile-block-init]');
    if (!scripts.length) return [];
    var src = scripts[0].textContent;
    var templates = [];
    var re = /pointsText(?:LoggedIn|LoggedOut):\s*safeLiquidToDecodedJsString\("([^"]*)"\)/g;
    var m;
    while ((m = re.exec(src)) !== null) {
      try {
        var div = document.createElement('div');
        div.innerHTML = decodeURIComponent(m[1]);
        var text = (div.textContent || '').replace(/\s+/g, ' ').trim();
        if (text.indexOf('[points_amount]') !== -1) templates.push(text);
      } catch (e) { /* skip malformed */ }
    }
    return templates;
  }

  function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* Match rendered text against a template; returns the substituted number string. */
  function extractPoints(renderedText, templates) {
    var text = renderedText.replace(/\s+/g, ' ').trim();
    for (var i = 0; i < templates.length; i++) {
      // Smile substitutes [points_amount] with "130 Points" (number + label),
      // so allow a lazy tail after the number to absorb the label word(s).
      var pattern = escapeRegex(templates[i]).replace(/\\\[points_amount\\\]/g, '([\\d,]+)[\\s\\S]*?');
      var m = text.match(new RegExp(pattern));
      if (m) return m[1];
    }
    return null;
  }

  /* Find the text node inside p that holds the points number. */
  function findPointsTextNode(p, numberString) {
    var walker = document.createTreeWalker(p, NodeFilter.SHOW_TEXT);
    var node;
    while ((node = walker.nextNode())) {
      if (node.parentElement && node.parentElement.closest('strong')) continue;
      if (node.data.indexOf(numberString) !== -1) return node;
    }
    return null;
  }

  function install(bars) {
    var p = document.querySelector(SMILE_P_SELECTOR);
    if (!p) return false;
    var templates = getTextTemplates();
    if (!templates.length) return false;
    if (p.textContent.indexOf('[points_amount]') !== -1) return false; // Smile not rendered yet

    var initialString = extractPoints(p.textContent, templates);
    if (!initialString) return false;
    var basePoints = parseInt(initialString.replace(/,/g, ''), 10);
    if (!basePoints) return false;

    function selectedRadio() {
      var radios = document.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < radios.length; i++) {
        if (!radios[i].checked) continue;
        for (var j = 0; j < bars.length; j++) {
          if (bars[j].id === radios[i].value) return { radio: radios[i], bar: bars[j] };
        }
      }
      return null;
    }

    function parseMoney(text) {
      var m = (text || '').match(/[\d.,]+/);
      if (!m) return null;
      var n = parseFloat(m[0].replace(/,/g, ''));
      return isFinite(n) && n > 0 ? n : null;
    }

    /* Discounted-vs-full price ratio from what Kaching displays on the bar.
       Currency-agnostic, so it covers fixed-amount ("save $20") and any
       other discount type without knowing how the config encodes it. */
    function displayedPriceRatio(radio) {
      var barEl = radio.closest('.kaching-bundles__bar');
      if (!barEl) return null;
      var price = parseMoney(barEl.querySelector('.kaching-bundles__bar-price') && barEl.querySelector('.kaching-bundles__bar-price').textContent);
      var full = parseMoney(barEl.querySelector('.kaching-bundles__bar-full-price') && barEl.querySelector('.kaching-bundles__bar-full-price').textContent);
      if (!price || !full || price > full) return null;
      return price / full;
    }

    function update() {
      var sel = selectedRadio();
      if (!sel) return;
      var bar = sel.bar;
      var factor;
      if (!bar.discountType || bar.discountType === 'default') {
        factor = 1;
      } else if (bar.discountType === 'percentage') {
        // Exact math for the common case (displayed prices are rounded
        // by Kaching's price-rounding setting, so avoid them here).
        factor = 1 - (bar.discountValue || 0) / 100;
      } else {
        // Unknown discount type (fixed amount, set price, ...): derive the
        // factor from Kaching's own displayed prices. Falls back to
        // undiscounted if the price elements can't be read.
        var ratio = displayedPriceRatio(sel.radio);
        factor = ratio !== null ? ratio : 1;
      }
      var next = Math.round(basePoints * bar.quantity * factor).toLocaleString();
      // Stateless: re-locate the paragraph and number on every update, so the
      // bridge survives Smile re-rendering its block (theme editor, app
      // re-hydration) — a held node reference would silently go dead.
      var liveP = document.querySelector(SMILE_P_SELECTOR);
      if (!liveP) return;
      var current = extractPoints(liveP.textContent, templates);
      if (!current || current === next) return;
      var node = findPointsTextNode(liveP, current);
      if (!node) return;
      node.data = node.data.replace(current, next);
    }

    document.addEventListener('change', function (e) {
      if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
        setTimeout(update, 50);
      }
    });
    // Re-apply if Smile re-renders the block and resets the number. Loop-safe:
    // update() no-ops once the displayed number already matches, so our own
    // write triggers at most one extra (idle) pass.
    var wrapper = document.querySelector('[id*="smile_io"]');
    if (wrapper && window.MutationObserver) {
      var pending = null;
      new MutationObserver(function () {
        if (pending) return;
        pending = setTimeout(function () { pending = null; update(); }, 100);
      }).observe(wrapper, { childList: true, subtree: true, characterData: true });
    }
    update();
    return true;
  }

  function waitAndInstall(bars, deadline) {
    if (install(bars)) return;
    if (Date.now() < deadline) {
      setTimeout(function () { waitAndInstall(bars, deadline); }, 250);
    }
  }

  function init() {
    var bars = getDealBars();
    if (!bars) return;
    if (isMultiVariant()) return; // Smile watches these itself; don't fight it
    waitAndInstall(bars, Date.now() + 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
